import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clock, RotateCcw, ArrowLeft, GitBranch } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const diffFields = (a, b) => {
  if (!a || !b) return [];
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).filter((k) => !["revisions", "id", "_id"].includes(k));
  const out = [];
  for (const k of keys) {
    const va = a[k], vb = b[k];
    if (JSON.stringify(va) !== JSON.stringify(vb)) out.push({ key: k, before: va, after: vb });
  }
  return out;
};

const PlanVersionHistory = () => {
  const { plan_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]); // up to 2 versions to compare
  const [snapshots, setSnapshots] = useState({});
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/plans/${plan_id}/versions`);
      setData(data);
    } catch (e) { toast.error("Failed to load history", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [plan_id]);

  const toggleSelect = async (v) => {
    setSelected((s) => {
      if (s.includes(v)) return s.filter((x) => x !== v);
      return s.length >= 2 ? [s[1], v] : [...s, v];
    });
    if (!snapshots[v]) {
      try {
        const { data } = await api.get(`/admin/plans/${plan_id}/versions/${v}`);
        setSnapshots((prev) => ({ ...prev, [v]: data.snapshot }));
      } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
    }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore this plan to version ${v}? A new version will be created preserving history.`)) return;
    setRestoring(v);
    try {
      const { data } = await api.post(`/admin/plans/${plan_id}/versions/${v}/restore`);
      toast.success(`Restored to v${v}`, { description: `New version: ${data.version}` });
      load();
    } catch (e) { toast.error("Restore failed", { description: e.response?.data?.detail || "" }); }
    finally { setRestoring(null); }
  };

  const [a, b] = selected;
  const diff = (a && b && snapshots[a] && snapshots[b]) ? diffFields(snapshots[a], snapshots[b]) : [];

  return (
    <AdminLayout title="Version history" actions={
      <Link to={`/admin/plans/${plan_id}/edit`} data-testid="back-to-plan" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-2"><ArrowLeft size={14} /> Back to plan</Link>
    }>
      {loading && <div className="text-muted-foreground">Loading…</div>}
      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          <div className="border border-border bg-card">
            <div className="px-4 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <GitBranch size={12} /> Revisions ({data.revisions.length})
            </div>
            {data.revisions.length === 0 && <div className="p-6 text-sm text-muted-foreground" data-testid="no-versions">No revisions yet.</div>}
            {data.revisions.map((r) => {
              const isSelected = selected.includes(r.version);
              return (
                <div key={r.version} data-testid={`version-row-${r.version}`} className={`p-4 border-b border-border last:border-b-0 ${isSelected ? "bg-accent/5" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">v{r.version} {r.version === data.current_version && <span className="ml-2 text-[10px] uppercase tracking-widest text-accent">current</span>}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Clock size={10} /> {fmt(r.timestamp)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{r.user_name}</div>
                      <div className="text-[11px] mt-1 italic">{r.summary}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button data-testid={`select-version-${r.version}`} onClick={() => toggleSelect(r.version)} className={`text-[11px] px-2 py-1 border ${isSelected ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent"}`}>
                        {isSelected ? "Selected" : "Compare"}
                      </button>
                      {r.snapshot && r.version !== data.current_version && (
                        <button data-testid={`restore-version-${r.version}`} onClick={() => restore(r.version)} disabled={restoring === r.version} className="text-[11px] px-2 py-1 border border-border hover:border-accent text-muted-foreground hover:text-accent disabled:opacity-40 flex items-center gap-1">
                          <RotateCcw size={10} /> {restoring === r.version ? "Restoring…" : "Restore"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border border-border bg-card">
            <div className="px-4 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
              {selected.length < 2 ? "Select two versions to compare" : `Diff v${selected[0]} → v${selected[1]}`}
            </div>
            {selected.length < 2 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="compare-hint">Click &ldquo;Compare&rdquo; on two versions.</div>}
            {selected.length === 2 && diff.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="compare-nodiff">No differences between these versions.</div>}
            {selected.length === 2 && diff.length > 0 && (
              <div data-testid="compare-diff" className="divide-y divide-border">
                {diff.map((d) => (
                  <div key={d.key} className="p-4">
                    <div className="overline text-accent">{d.key}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-xs">
                      <div className="border border-red-200 bg-red-50 p-3 whitespace-pre-wrap break-words"><div className="text-[10px] uppercase text-red-700 mb-1">v{selected[0]}</div>{JSON.stringify(d.before, null, 2)}</div>
                      <div className="border border-emerald-200 bg-emerald-50 p-3 whitespace-pre-wrap break-words"><div className="text-[10px] uppercase text-emerald-700 mb-1">v{selected[1]}</div>{JSON.stringify(d.after, null, 2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PlanVersionHistory;
